'use client';

import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { FilterTier } from '@/lib/search-gating';
import type { FilterParams } from '@/lib/search-filter-params';
import type { NaceCode } from './searchSidebarOptions';
import { SidebarFooter } from './filter-groups/SidebarFooter';
import { sidebarAsideClassName } from './searchSidebarHelpers';
import { SidebarMobileHeader } from './filter-groups/SidebarMobileHeader';
import { useFilterPickerState, useSearchFacets, useSidebarOptions } from './useSidebarPickers';
import { countActiveFilters, useCollectFilters, useFilterGroupClears, useSidebarFilterValues } from './useSidebarFilterState';
import { type SidebarGroupsContext, SidebarFilterGroups, SidebarKeywordSearch } from './SidebarFilterGroups';
import { useSidebarKeywordSearch } from './useSidebarKeywordSearch';

interface CompanySearchSidebarProps {
  naceCodes: NaceCode[];
  cityList: string[];
  tagList: string[];
  onSubmit: (filters: FilterParams) => void;
  sort?: string;
  offset?: number;
  limit?: number;
  totalLimit?: number;
  onTotalLimitChange?: (limit: number) => void;
  /** Show the fiscal-year picker. The value itself rides in FilterParams. */
  showFinancialYear?: boolean;
  total?: number;
  onOffsetChange?: (offset: number) => void;
  filterParams?: FilterParams;
  isOpen?: boolean;
  onClose?: () => void;
  onCountryChange?: (country: string) => void;
  /** When true, the sidebar renders as a fullscreen overlay on mobile (with
   *  a top close button and the company-search input visible). Used by the
   *  /search page; other consumers (e.g. radar) keep the legacy inline
   *  dropdown behavior. */
  mobileFullscreen?: boolean;
  /**
   * Which filters this visitor may use — and, at `'public'`, whether the
   * mobile pagination block is offered at all.
   *
   * One prop rather than the `isAuthenticated` + `isPro` pair it replaces:
   * the two were separate booleans encoding a single ordered value, so every
   * consumer had to re-derive the tier and could disagree about it.
   */
  tier?: FilterTier;
  hideCompanySearch?: boolean;
  hidePersonSearch?: boolean;
  hideNaceCodes?: boolean;
  /**
   * Save the staged filter set.
   *
   * The sidebar holds *staged* state while the results on screen come from
   * the last applied search, so the page applies the filters as part of
   * saving them — otherwise a user who tweaked a control without pressing
   * Search would silently save something other than what they are looking
   * at. Absent (radar, /listen) the control is not rendered at all.
   */
  onSaveSearch?: (filters: FilterParams) => void;
  /** The user's saved searches, for the quick-load menu. */
  savedSearches?: { id: number; name: string; filters: FilterParams }[];
  onLoadSavedSearch?: (id: number) => void;
  /** True when the staged filters already match a saved search. */
  isCurrentSearchSaved?: boolean;
}

export default function CompanySearchSidebar({
  naceCodes,
  cityList,
  tagList,
  onSubmit,
  offset = 0,
  totalLimit = 50,
  showFinancialYear = false,
  total = 0,
  onOffsetChange,
  filterParams,
  isOpen = true,
  onClose,
  onCountryChange,
  mobileFullscreen = false,
  tier = 'public',
  hideCompanySearch = false,
  hidePersonSearch = false,
  hideNaceCodes = false,
  onSaveSearch,
  savedSearches,
  onLoadSavedSearch,
  isCurrentSearchSaved = false,
}: CompanySearchSidebarProps) {
  const { t, i18n } = useTranslation();
  const fmtLocale = i18n.resolvedLanguage || i18n.language || 'en';
  // Hook order mirrors the effect order the inline version had: facets fetch,
  // CPV + court fetches, the filterParams sync, then the autocomplete effects.
  const facets = useSearchFacets();
  const pickers = useFilterPickerState();

  const preserveCountryEdits = Boolean(onCountryChange);
  // One staged-filter object, driven by FILTER_DEFS (lib/search-filter-defs.ts).
  const filterValues = useSidebarFilterValues(filterParams, preserveCountryEdits);
  const { staged, set } = filterValues;
  const { country } = staged;

  const { facetCount, facetSum } = facets;
  const { naceSearch, expandedNaceNodes } = pickers;
  const options = useSidebarOptions({
    country, facetCount, facetSum, i18n, fmtLocale, naceCodes, naceSearch, expandedNaceNodes, tier,
  });

  const collectFilters = useCollectFilters(filterValues);
  const counts = countActiveFilters(filterValues);
  const clears = useFilterGroupClears(filterValues);

  const keyword = useSidebarKeywordSearch(onSubmit, collectFilters);
  const groupsContext: SidebarGroupsContext = {
    t, i18n, values: filterValues, pickers, options, facets, counts, clears,
    handleFormSubmit: keyword.handleFormSubmit,
    naceCodes, cityList, tagList, hideNaceCodes, hidePersonSearch, showFinancialYear, onCountryChange,
  };

  return (
    <aside
      id="search-filters"
      aria-label={t('search.filters')}
      className={sidebarAsideClassName(mobileFullscreen, isOpen)}
    >
      {/* Mobile-only top bar with a close button — fullscreen-overlay mode
          only. lg:hidden so desktop keeps the inline-panel chrome. */}
      {mobileFullscreen && onClose && <SidebarMobileHeader t={t} onClose={onClose} />}
      {/* Inner wrapper keeps content at fixed width during desktop collapse */}
      <div
        className={cn(
          'flex flex-col w-full lg:w-[380px] lg:min-w-[380px]',
          // In fullscreen-overlay mode the wrapper needs to fill the aside so
          // the footer Search button stays pinned at the bottom. Legacy mode
          // keeps the natural-height layout for radar's inline dropdown.
          mobileFullscreen ? 'flex-1 min-h-0' : 'lg:h-full',
        )}
      >
        {/* Scrollable Filters. `overscroll-contain` stops iOS Safari from
            bubble-scrolling the page underneath when the user hits the top/
            bottom of the list inside the mobile filter overlay. */}
        <div className="flex-1 overflow-y-auto overscroll-contain min-h-0 p-4 flex flex-col">
          {!hideCompanySearch && (
            <SidebarKeywordSearch
              t={t}
              keyword={keyword}
              searchInput={staged.q}
              setSearchInput={set.q}
            />
          )}

          {/* ─────────────────────── Filter groups ─────────────────────── */}
          <SidebarFilterGroups ctx={groupsContext} />

        </div>

        {/* Footer Actions */}
        <SidebarFooter
          onSaveSearch={onSaveSearch}
          collectFilters={collectFilters}
          savedSearches={savedSearches}
          isCurrentSearchSaved={isCurrentSearchSaved}
          t={t}
          onLoadSavedSearch={onLoadSavedSearch}
          handleFormSubmit={keyword.handleFormSubmit}
          tier={tier}
          onOffsetChange={onOffsetChange}
          offset={offset}
          totalLimit={totalLimit}
          total={total}
        />
      </div>
    </aside>
  );
}
